import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type {
  CreateFactoryPayload,
  UpdateFactoryPayload,
  UpdateOrganizationPayload,
} from "./api";
import {
  createFactory,
  deleteFactory,
  getFactories,
  getMembers,
  getMembersLookup,
  getOrganization,
  inviteMember,
  revokeMember,
  updateFactory,
  updateMemberAccess,
  updateOrganization,
} from "./api";

export function useMembers(orgId?: string | null) {
  return useQuery({
    queryKey: ["organization", orgId, "members"],
    queryFn: () => getMembers(orgId!),
    enabled: !!orgId,
  });
}

/** memberId/userId -> display name map, keyed by both ids since callers store either. */
export function useMemberLookupQuery(orgId?: string | null) {
  return useQuery({
    queryKey: ["organization", orgId, "members", "lookup"],
    queryFn: async () => {
      const res = await getMembersLookup(orgId!);
      const map = new Map<string, string>();
      for (const entry of res.data) {
        if (entry.name) {
          map.set(entry.memberId, entry.name);
          map.set(entry.userId, entry.name);
        }
      }
      return map;
    },
    enabled: !!orgId,
    staleTime: 10 * 60 * 1000,
  });
}

export function useInviteMemberMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      orgId,
      email,
      role,
    }: {
      orgId: string;
      email: string;
      role: string;
    }) => inviteMember(orgId, email, role),
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries({
        queryKey: ["organization", orgId, "members"],
      });
    },
  });
}

export function useRevokeMemberMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ orgId, memberId }: { orgId: string; memberId: string }) =>
      revokeMember(orgId, memberId),
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries({
        queryKey: ["organization", orgId, "members"],
      });
    },
  });
}

export function useUpdateMemberAccessMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      orgId,
      memberId,
      role,
    }: {
      orgId: string;
      memberId: string;
      role: string;
    }) => updateMemberAccess(orgId, memberId, role),
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries({
        queryKey: ["organization", orgId, "members"],
      });
    },
  });
}

export function useOrganizationQuery(orgId?: string | null) {
  return useQuery({
    queryKey: ["organization", orgId, "details"],
    queryFn: () => getOrganization(orgId!),
    enabled: !!orgId,
  });
}

export function useUpdateOrganizationMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      orgId,
      payload,
    }: {
      orgId: string;
      payload: UpdateOrganizationPayload;
    }) => updateOrganization(orgId, payload),
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries({
        queryKey: ["organization", orgId, "details"],
      });
    },
  });
}

export function useFactoriesQuery(orgId?: string | null, page = 1, limit = 10) {
  return useQuery({
    queryKey: ["organization", orgId, "factories", page, limit],
    queryFn: () => getFactories(orgId!, page, limit),
    enabled: !!orgId,
  });
}

export function useCreateFactoryMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      orgId,
      payload,
    }: {
      orgId: string;
      payload: CreateFactoryPayload;
    }) => createFactory(orgId, payload),
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries({
        queryKey: ["organization", orgId, "factories"],
      });
    },
  });
}

export function useUpdateFactoryMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      orgId,
      factoryId,
      payload,
    }: {
      orgId: string;
      factoryId: string;
      payload: UpdateFactoryPayload;
    }) => updateFactory(orgId, factoryId, payload),
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries({
        queryKey: ["organization", orgId, "factories"],
      });
    },
  });
}

export function useDeleteFactoryMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ orgId, factoryId }: { orgId: string; factoryId: string }) =>
      deleteFactory(orgId, factoryId),
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries({
        queryKey: ["organization", orgId, "factories"],
      });
    },
  });
}
